import { createFileRoute, Link } from '@tanstack/react-router';

import { Breadcrumb, Card, Markdown, PageHeader, PageLayout, Skeleton } from '@keboola/design';

import { QueryError } from '../../../components/QueryError';
import { AppIcon } from '../../../features/apps/components/AppIcon';
import { ComponentBadges } from '../../../features/apps/components/ComponentBadges';
import { PublicFlags } from '../../../features/apps/components/PublicFlags';
import { getAppTitle } from '../../../features/apps/helpers';
import { usePublishedApp } from '../../../features/apps/queries';

export const Route = createFileRoute('/_public/components/$appId')({
  component: ComponentDetailPage,
  // The id, not the name: `head` runs before the query resolves.
  head: ({ params }) => ({
    meta: [{ title: `${params.appId} | Keboola Developer Portal` }],
  }),
});

function ComponentDetailPage() {
  const { appId } = Route.useParams();
  const { data: app, isLoading, isError, error, refetch } = usePublishedApp(appId);

  // The id is all that is known before the app loads.
  const title = app === undefined ? appId : getAppTitle(app);

  return (
    <PageLayout>
      <PageLayout.Header>
        <PageHeader.Detail
          breadcrumbs={
            <Breadcrumb>
              <Breadcrumb.List>
                <Breadcrumb.Item>
                  <Breadcrumb.Link asChild>
                    <Link to="/components">Components</Link>
                  </Breadcrumb.Link>
                </Breadcrumb.Item>
                <Breadcrumb.Separator />
                <Breadcrumb.Item>
                  <Breadcrumb.Page>{title}</Breadcrumb.Page>
                </Breadcrumb.Item>
              </Breadcrumb.List>
            </Breadcrumb>
          }
          titleIcon={app === undefined ? undefined : <AppIcon app={app} size={48} />}
          title={title}
          badges={app === undefined ? undefined : <ComponentBadges app={app} />}
          description={app?.shortDescription}
        />
      </PageLayout.Header>

      {isLoading && (
        <div className="tw:flex tw:flex-col tw:gap-4">
          <Skeleton className="tw:h-40" />
          <Skeleton className="tw:h-24" />
        </div>
      )}

      {/* Error and data can coexist: the page is seeded from the catalog's cache. */}
      <QueryError
        isError={isError}
        hasData={app !== undefined}
        error={error}
        onRetry={() => void refetch()}
        loadTitle="Could not load this component"
        refreshTitle="Could not refresh this component"
        staleNote="Showing the version from the last successful load."
        notFound={{
          title: 'This component does not exist',
          description:
            'It may have been removed, or the link may be out of date. Browse the catalog to find what you are looking for.',
        }}
        titleAs="h2"
      />

      {app !== undefined && (
        <div className="tw:flex tw:flex-col tw:gap-6">
          <div className="tw:grid tw:grid-cols-1 tw:gap-6 tw:lg:grid-cols-3">
            <Card className="tw:lg:col-span-2">
              <Card.Content>
                <PublicFlags app={app} />
              </Card.Content>
            </Card>

            <Card>
              <Card.Content>
                <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">Developer</p>
                <p className="tw:m-0 tw:font-semibold tw:text-foreground">
                  {app.vendor.name ?? app.vendor.id}
                </p>
              </Card.Content>
            </Card>
          </div>

          {typeof app.longDescription === 'string' && app.longDescription !== '' && (
            <div className="tw:w-full tw:max-w-3xl">
              <Markdown rhythm="chrome">{app.longDescription}</Markdown>
            </div>
          )}
        </div>
      )}
    </PageLayout>
  );
}
